import { z } from 'zod';

import { PageStatus, PageTemplate } from '@nucleus-commerce/api-client/admin';

import { seoOverrideSchema } from '@/features/seo';

const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export const MAX_FAQ_ITEMS = 100;
export const MAX_STEPS = 50;

export const faqItemSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.questionRequired' })
    .max(300, { error: 'pages:validation.questionLength' }),
  answer: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.answerRequired' })
    .max(4000, { error: 'pages:validation.answerLength' }),
});

export const stepSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.stepNameRequired' })
    .max(200, { error: 'pages:validation.stepNameLength' }),
  text: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.stepTextRequired' })
    .max(4000, { error: 'pages:validation.stepTextLength' }),
});

/** The hero is optional on every template; `enabled` is form state only. */
export const heroSchema = z
  .object({
    enabled: z.boolean(),
    heading: z.string().trim().max(200, { error: 'pages:validation.headingLength' }),
    subheading: z.string().trim().max(400, { error: 'pages:validation.subheadingLength' }),
    mediaId: z.string().nullable(),
  })
  .refine((hero) => !hero.enabled || hero.heading !== '', {
    path: ['heading'],
    error: 'pages:validation.headingRequired',
  });

export const pageFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.titleRequired' })
    .max(200, { error: 'pages:validation.titleLength' }),
  handle: z
    .string()
    .trim()
    .min(1, { error: 'pages:validation.handleRequired' })
    .max(120, { error: 'pages:validation.handleLength' })
    .regex(HANDLE, { error: 'pages:validation.handleFormat' }),
  bodyHtml: z.string().max(200_000, { error: 'pages:validation.bodyLength' }),
  template: z.enum(PageTemplate),
  status: z.enum(PageStatus),
  blocks: z.object({
    faq: z.array(faqItemSchema).max(MAX_FAQ_ITEMS, { error: 'pages:validation.faqMax' }),
    steps: z.array(stepSchema).max(MAX_STEPS, { error: 'pages:validation.stepsMax' }),
    hero: heroSchema,
  }),
  seo: seoOverrideSchema,
});

export type PageFormInput = z.input<typeof pageFormSchema>;
export type PageFormValues = z.output<typeof pageFormSchema>;

/** Which list blocks a template renders; the hero works on every template. */
export function templateBlocks(template: PageTemplate): { faq: boolean; steps: boolean } {
  return { faq: template === 'faq', steps: template === 'size_guide' };
}
